/**
 * GuestFocusGroup — the focus-group discussion for a verified guest on a shared
 * study link.
 *
 * This is the authenticated engine, unchanged: the same `useChatRoom` (SWR +
 * Socket.IO) and the same `FocusGroupSession`/`ChatThread` the report owner
 * gets. Only the HTTP surface differs, and that is injected as a transport.
 */
import { useMemo } from 'react'
import { ArrowLeft } from 'lucide-react'

import { useChatRoom } from '@/pages/ChatPage/useChatRoom'
import { createGuestChatRoomTransport } from '@/pages/ChatPage/chatRoomTransport'
import { FocusGroupSession } from '@/pages/ChatPage/FocusGroupSession/FocusGroupSession'
import { getChatPlaceholders } from '@/lib/constants/languages'

interface GuestFocusGroupProps {
  shareToken: string
  roomToken: string
  onBack: () => void
}

export function GuestFocusGroup({
  shareToken,
  roomToken,
  onBack,
}: GuestFocusGroupProps) {
  const transport = useMemo(
    () => createGuestChatRoomTransport(shareToken),
    [shareToken],
  )

  const {
    roomData,
    participants,
    roomStatus,
    roomTitle,
    messages,
    thinkingParticipantIds,
    reactions,
    liveReactionKeys,
    isSending,
    isWaitingForTurn,
    queueBlockedReason,
    messageQueue,
    handleSend,
    handleQueueEdit,
    handleQueueRemove,
    handleQueueRetry,
    blockedQueuedId,
    sendError,
    messagesError,
    retryMessages,
  } = useChatRoom(roomToken, { enableRouteFallback: false, transport })

  const emptyHeadline = getChatPlaceholders(roomData?.settings.language ?? 'en').emptyHeadlineGroup

  return (
    <div
      className="flex flex-1 min-h-0 flex-col gap-3"
      data-testid="guest-focus-group"
    >
      <button
        type="button"
        onClick={onBack}
        className="flex w-fit items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        data-testid="guest-focus-group-back"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to audience
      </button>

      <FocusGroupSession
        title={roomTitle || 'Focus group'}
        participants={participants}
        // No authenticated persona popovers on a share link.
        populationId={null}
        roomStatus={roomStatus}
        messages={messages}
        thinkingParticipantIds={thinkingParticipantIds}
        isSending={isSending}
        isWaitingForTurn={isWaitingForTurn}
        queueBlockedReason={queueBlockedReason}
        onEnd={onBack}
        onSend={handleSend}
        messageQueue={messageQueue}
        onQueueEdit={handleQueueEdit}
        onQueueRemove={handleQueueRemove}
        onQueueRetry={handleQueueRetry}
        blockedQueuedId={blockedQueuedId}
        sendError={sendError}
        messagesError={messagesError}
        onRetryMessages={retryMessages}
        fullHeight
        emptyHeadline={emptyHeadline}
        roomToken={roomToken}
        getImageUrl={transport.getImageUrl}
        reactions={reactions}
        liveReactionKeys={liveReactionKeys}
      />
    </div>
  )
}
